import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import { GUIDES, type Guide } from "@/lib/guides";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

interface Props {
  /** Guide slugs to render. If omitted, renders the top 4. */
  slugs?: string[];
  title?: string;
  /** Show "Need help? Call Woodseer" secondary line on each card. */
  showCallCta?: boolean;
  /** Slim variant used by service pages. */
  compact?: boolean;
}

const DEFAULT_SLUGS = [
  "mot-warning-lights-before-test",
  "tyre-check-before-mot",
  "brake-noise-squeaking-grinding",
  "engine-light-east-london",
];

export function HelpfulGuidesGrid({ slugs, title = "Before You Book: Free Checks & Advice", showCallCta = true, compact = false }: Props) {
  const cfg = getBusinessConfig();
  const picks: Guide[] = (slugs ?? DEFAULT_SLUGS)
    .map((s) => GUIDES.find((g) => g.slug === s))
    .filter((x): x is Guide => !!x);
  if (picks.length === 0) return null;

  return (
    <section className={compact ? "py-10" : "py-14 md:py-16"}>
      <div className="mx-auto max-w-5xl px-4">
        <div className="flex items-end justify-between gap-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">{title}</h2>
          <Link to="/motorist-help" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            All guides<ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {!compact && (
          <p className="mt-2 text-sm text-muted-foreground">
            Practical advice from an independent East London garage — no scare tactics, no jargon.
          </p>
        )}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {picks.map((g) => (
            <div key={g.slug} className="rounded-2xl border border-border bg-card p-5 flex flex-col">
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-primary">{g.category}</p>
              <h3 className="mt-2 text-base font-bold text-foreground leading-snug">{g.title}</h3>
              <p className="mt-2 text-xs text-muted-foreground line-clamp-3">{g.excerpt}</p>
              <div className="mt-4 flex items-center justify-between gap-2">
                <Link
                  to="/guide/$slug"
                  params={{ slug: g.slug }}
                  className="text-sm font-bold text-primary hover:underline inline-flex items-center gap-1"
                  onClick={() => trackEvent("guide_card_click", { slug: g.slug })}
                >
                  Read guide<ArrowRight className="h-3.5 w-3.5" />
                </Link>
                {showCallCta && (
                  <a
                    href={cfg.phoneHref}
                    className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
                    onClick={() => trackEvent("guide_card_call", { slug: g.slug })}
                  >
                    <Phone className="h-3 w-3" />Call
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 md:hidden text-center">
          <Link to="/motorist-help" className="inline-flex items-center gap-1 text-sm font-semibold text-primary">All guides<ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}
